import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { Crown, Plus, Trash2, Shield, Server, Database, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

export default function CrownJewels() {
  const { isDarkMode } = useTheme();
  const [crownJewels, setCrownJewels] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newCrownJewel, setNewCrownJewel] = useState({
    asset_id: '',
    business_impact: 'critical',
    data_classification: 'restricted',
    business_unit: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cjRes, assetsRes] = await Promise.all([
        api.get('/crown-jewels'),
        api.get('/assets')
      ]);
      setCrownJewels(cjRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      console.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/crown-jewels', newCrownJewel);
      toast.success('Crown Jewel tagged successfully');
      setIsDialogOpen(false);
      setNewCrownJewel({ asset_id: '', business_impact: 'critical', data_classification: 'restricted', business_unit: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to tag Crown Jewel');
    } finally {
      setCreating(false);
    }
  };

  const getImpactColor = (impact) => {
    const colors = {
      critical: isDarkMode ? 'text-red-400 bg-red-500/20 border-red-500/30' : 'text-red-600 bg-red-100 border-red-200',
      high: isDarkMode ? 'text-orange-400 bg-orange-500/20 border-orange-500/30' : 'text-orange-600 bg-orange-100 border-orange-200',
      medium: isDarkMode ? 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30' : 'text-yellow-600 bg-yellow-100 border-yellow-200',
      low: isDarkMode ? 'text-green-400 bg-green-500/20 border-green-500/30' : 'text-green-600 bg-green-100 border-green-200'
    };
    return colors[impact] || colors.medium;
  };

  const getClassificationColor = (classification) => {
    const colors = {
      restricted: isDarkMode ? 'text-red-400' : 'text-red-600',
      confidential: isDarkMode ? 'text-orange-400' : 'text-orange-600',
      internal: isDarkMode ? 'text-yellow-400' : 'text-yellow-600',
      public: isDarkMode ? 'text-green-400' : 'text-green-600'
    };
    return colors[classification] || (isDarkMode ? 'text-slate-400' : 'text-slate-500');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className={`w-8 h-8 border-2 border-t-transparent rounded-full animate-spin ${isDarkMode ? 'border-yellow-500' : 'border-yellow-600'}`}></div>
          <span className={`text-sm font-inter ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Loading Crown Jewels...</span>
        </div>
      </div>
    );
  }

  // Get non-tagged assets
  const taggedAssetIds = crownJewels.map(cj => cj.asset_id);
  const availableAssets = assets.filter(a => !taggedAssetIds.includes(a.id));

  return (
    <div data-testid="crown-jewels-container" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isDarkMode 
              ? 'bg-yellow-500/15 border border-yellow-500/30' 
              : 'bg-yellow-100 border border-yellow-200'
          }`}>
            <Crown className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
          </div>
          <div>
            <h1 className={`font-inter font-bold text-xl lg:text-2xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Crown Jewels
            </h1>
            <p className={`text-sm font-inter mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Business-critical assets requiring enhanced protection
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className={isDarkMode ? 'border-navy-700 text-slate-300 hover:bg-navy-800 hover:border-electric-500/50' : ''}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-yellow-600 hover:bg-yellow-500 text-white" disabled={availableAssets.length === 0}>
                <Plus className="w-4 h-4 mr-2" />
                Tag Crown Jewel
              </Button>
            </DialogTrigger>
            <DialogContent className={isDarkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-slate-200 text-slate-900'}>
              <DialogHeader>
                <DialogTitle className="font-inter text-xl flex items-center gap-2">
                  <Crown className={`w-6 h-6 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  Tag Asset as Crown Jewel
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Asset</Label>
                  <Select value={newCrownJewel.asset_id} onValueChange={(v) => setNewCrownJewel({...newCrownJewel, asset_id: v})}>
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-navy-800 border-navy-700 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                      <SelectValue placeholder="Select asset" />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                      {availableAssets.map(asset => (
                        <SelectItem key={asset.id} value={asset.id}>{asset.name} ({asset.type})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Business Impact</Label>
                  <Select value={newCrownJewel.business_impact} onValueChange={(v) => setNewCrownJewel({...newCrownJewel, business_impact: v})}>
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-navy-800 border-navy-700 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Data Classification</Label>
                  <Select value={newCrownJewel.data_classification} onValueChange={(v) => setNewCrownJewel({...newCrownJewel, data_classification: v})}>
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-navy-800 border-navy-700 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                      <SelectItem value="restricted">Restricted</SelectItem>
                      <SelectItem value="confidential">Confidential</SelectItem>
                      <SelectItem value="internal">Internal</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={creating || !newCrownJewel.asset_id} className="w-full bg-yellow-600 hover:bg-yellow-500 text-white">
                  {creating ? 'Tagging...' : 'Tag as Crown Jewel'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-navy-800/50 border-navy-700' : 'bg-white border-slate-200 shadow-card'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-yellow-500/15 border border-yellow-500/30' : 'bg-yellow-100 border border-yellow-200'}`}>
              <Crown className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{crownJewels.length}</p>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Total Crown Jewels</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-navy-800/50 border-navy-700' : 'bg-white border-slate-200 shadow-card'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-500/15 border border-red-500/30' : 'bg-red-100 border border-red-200'}`}>
              <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{crownJewels.filter(cj => cj.business_impact === 'critical').length}</p>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Critical Impact</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-navy-800/50 border-navy-700' : 'bg-white border-slate-200 shadow-card'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-violet-500/15 border border-violet-500/30' : 'bg-violet-100 border border-violet-200'}`}>
              <Shield className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{crownJewels.filter(cj => cj.data_classification === 'restricted').length}</p>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Restricted Data</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-navy-800/50 border-navy-700' : 'bg-white border-slate-200 shadow-card'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200'}`}>
              <Server className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-inter font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{availableAssets.length}</p>
              <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Available to Tag</p>
            </div>
          </div>
        </div>
      </div>

      {/* Crown Jewels List */}
      {crownJewels.length === 0 ? (
        <div className={`p-12 rounded-xl border text-center ${isDarkMode ? 'bg-navy-800/50 border-navy-700' : 'bg-white border-slate-200 shadow-card'}`}>
          <Crown className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-yellow-400/50' : 'text-yellow-500/50'}`} />
          <h3 className={`font-inter font-semibold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No Crown Jewels Tagged</h3>
          <p className={`font-inter text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Tag your most critical assets to enable enhanced monitoring and protection.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {crownJewels.map((cj) => {
            const asset = assets.find(a => a.id === cj.asset_id);
            return (
              <div 
                key={cj.id} 
                className={`p-4 rounded-xl border transition-all ${isDarkMode ? 'bg-navy-800/50 border-navy-700 hover:border-yellow-500/50' : 'bg-white border-slate-200 shadow-card hover:shadow-card-hover'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-yellow-500/15 border border-yellow-500/30' : 'bg-yellow-100 border border-yellow-200'}`}>
                      {asset?.type === 'server' ? <Server className="w-5 h-5 text-yellow-400" /> :
                       asset?.type === 'database' ? <Database className="w-5 h-5 text-yellow-400" /> :
                       <Shield className="w-5 h-5 text-yellow-400" />}
                    </div>
                    <div>
                      <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {asset?.name || 'Unknown Asset'}
                      </h3>
                      <p className={`text-xs font-inter ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                        {asset?.type || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="icon"
                    variant="ghost"
                    className={isDarkMode ? 'hover:bg-red-500/20 text-slate-400 hover:text-red-400' : 'hover:bg-red-100 text-slate-500 hover:text-red-600'}
                    onClick={async () => {
                      try {
                        await api.delete(`/crown-jewels/${cj.id}`);
                        toast.success('Crown Jewel removed');
                        fetchData();
                      } catch (error) {
                        toast.error('Failed to remove');
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getImpactColor(cj.business_impact)}`}>
                    {cj.business_impact} impact
                  </span>
                  <span className={`px-2 py-1 rounded-md text-xs font-medium border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'} ${getClassificationColor(cj.data_classification)}`}>
                    {cj.data_classification}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
